import { createSelector } from "@reduxjs/toolkit";
import Drawer from "Common/Components/Drawer";
import { X } from "lucide-react";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { setFilterOptions } from "slices/jewellery/reducer";
import {
  getCategories as onGetCategories,
  getSubCategories as onGetSubCategories,
} from "slices/thunk";
import { getShapes as onGetShapes } from "slices/thunk";

const gender = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

interface FilterOptions {
  search?: string;
  gender?: "male" | "female";
  category?: string;
  subcategory?: string;
  shape?: string;
  onlyDiscounted?: boolean;
}

interface FilterProductsProps {
  drawerEnd: boolean;
  drawerEndToggle: () => void;
  filterOptions: FilterOptions;
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
}

const FilterProducts: React.FC<FilterProductsProps> = ({
  drawerEnd,
  drawerEndToggle,
  filterOptions,
  handleApplyFilters,
  handleResetFilters,
}) => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Jewellery,
    (state) => ({
      categories: state.categories,
      subcategories: state.subcategories,
      isLoading: state.isLoading,
    })
  );

  const { shapes } = useSelector((state: any) => state.Diamond);

  const { categories, subcategories, isLoading } = useSelector(selectDataList);

  const handleFilterChange = (
    type: "category" | "subcategory" | "shape" | "gender" | "onlyDiscounted",
    event: any
  ) => {
    const { value, checked } = event.target;
    if (type === "gender") {
      dispatch(
        setFilterOptions({
          ...filterOptions,
          [type]: value, // Only store the selected value for "gender"
        })
      );
      return;
    }
    if (type === "onlyDiscounted") {
      dispatch(
        setFilterOptions({
          ...filterOptions,
          [type]: checked, // Only store the selected value for "gender"
        })
      );
      return;
    }

    const currentFilterValue = filterOptions[type] || "";
    const updatedValue = checked
      ? currentFilterValue
        ? `${currentFilterValue},${value}`
        : value
      : currentFilterValue
          .split(",")
          .filter((item: string) => item !== value)
          .join(",");

    dispatch(
      setFilterOptions({
        ...filterOptions,
        [type]: updatedValue,
      })
    );
  };

  useEffect(() => {
    dispatch(onGetCategories());
    dispatch(onGetSubCategories());
    dispatch(onGetShapes());
  }, [dispatch]);

  return (
    <Drawer
      show={drawerEnd}
      onHide={drawerEndToggle}
      id="drawerEnd"
      drawer-end="true"
      className="fixed inset-y-0 flex flex-col w-full transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 sm:w-80 z-drawer dark:bg-zink-600"
    >
      <Drawer.Header
        className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
        closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
      >
        <Drawer.Title className="text-15">Filter</Drawer.Title>
        <X className="size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
      </Drawer.Header>
      <Drawer.Body className="h-full p-4 overflow-y-auto">
        <div className="card-body">
          {/* Caegory */}
          <div>
            <div className="pt-5">Caegory</div>
            {categories?.map((category: any) => {
              const selectCategory = filterOptions?.category
                ?.split(",")
                .includes(category._id);
              return (
                <div className="flex pt-3 items-center">
                  <input
                    //   checked
                    id={category?._id}
                    type="checkbox"
                    value={category._id}
                    onChange={(event: any) =>
                      handleFilterChange("category", event)
                    }
                    checked={selectCategory}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={category?._id}
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    {category?.name}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Sub Category */}
          <div>
            <div className="pt-5">Sub Category</div>
            {subcategories?.map((subcategory: any) => {
              const selectSubCategory = filterOptions?.subcategory
                ?.split(",")
                .includes(subcategory._id);
              return (
                <div className="flex pt-3 items-center">
                  <input
                    //   checked
                    id={subcategory?._id}
                    type="checkbox"
                    value={subcategory._id}
                    onChange={(event: any) =>
                      handleFilterChange("subcategory", event)
                    }
                    checked={selectSubCategory}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={subcategory?._id}
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    {subcategory?.name}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Shapes */}
          <div>
            <div className="pt-5">Shapes</div>
            {shapes?.map((shape: any) => {
              const selectShape = filterOptions?.shape
                ?.split(",")
                .includes(shape._id);
              return (
                <div className="flex pt-3 items-center">
                  <input
                    //   checked
                    id={shape?._id}
                    type="checkbox"
                    value={shape?._id}
                    onChange={(event: any) =>
                      handleFilterChange("shape", event)
                    }
                    checked={selectShape}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={shape?._id}
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    {shape?.shape}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Gender */}
          <div>
            <div className="pt-5">Gender</div>
            {gender?.map((gender: any) => {
              return (
                <div className="flex pt-3 items-center" key={gender.value}>
                  <input
                    //   checked
                    id={gender.value}
                    type="radio"
                    name="gender"
                    value={gender.value}
                    onChange={(event: any) =>
                      handleFilterChange("gender", event)
                    }
                    checked={filterOptions.gender === gender.value}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={gender.value}
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    {gender?.label}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Discounted */}
          <div>
            <div className="pt-5">Discounted</div>
            <div className="flex pt-3 items-center">
              <input
                //   checked
                id="onlyDiscounted"
                type="checkbox"
                value="onlyDiscounted"
                checked={filterOptions.onlyDiscounted || false}
                onChange={(event: any) =>
                  handleFilterChange("onlyDiscounted", event)
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="onlyDiscounted"
                className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                Only Discounted
              </label>
            </div>
          </div>
        </div>
      </Drawer.Body>
      <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500">
        <button
          type="reset"
          className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
          onClick={() => {
            handleResetFilters();
            drawerEndToggle();
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
          onClick={handleApplyFilters}
          disabled={isLoading}
        >
          Apply
          {/* {isLoading ? (
            <div className="flex">
              <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
              Applying...
            </div>
          ) : (
            "Apply"
          )} */}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
};

export default FilterProducts;
